import type { SessionWithDetails } from '../../../../services/api/sessions';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { formatTime } from '../../../../utils/formatters';
import { getTimezoneAbbreviation } from '../../../../utils/timezone';

/** Session shape expected by GroupCard */
export interface GroupCardSession {
  id: string;
  number: number;
  topic: string;
  date: string;
  time: string;
  coachName: string | null;
  status: string;
  // MED-1367: true when the session started before now or is completed.
  // Only the read-only coach card sets it.
  isPast?: boolean;
}

/**
 * Transform SessionWithDetails[] to the lighter GroupCardSession format.
 * Sessions are sorted ascending by session_number.
 */
export const transformSessionsForGroupCard = (
  sessions: SessionWithDetails[],
  coachName: string | null,
): GroupCardSession[] => {
  return sessions
    .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
    .map((session, index) => {
      let dateStr = '—';
      let timeStr = '—';

      if (session.scheduled_start) {
        const date = new Date(session.scheduled_start);
        // MDF-201: Render the session in ITS OWN timezone, not the viewer's.
        // Both calls previously omitted the zone, so every session read as
        // Eastern Time for an admin. A 9:00 AM India session displayed as
        // "9:00 AM ET", which looked correct and was not.
        //
        // The date needs the zone as much as the time does. Without it, a
        // session late in the evening in one zone shows the wrong day.
        dateStr = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: session.timezone || undefined,
        });
        timeStr = formatTime(session.scheduled_start, session.timezone);
      }

      const num = session.session_number || index + 1;
      return {
        id: session.id,
        number: num,
        // MED-1367: fall back to the session title before "(no topic)".
        topic: session.topic_name || session.title || `Session ${num} (no topic)`,
        date: dateStr,
        time: timeStr,
        coachName,
        status: session.status,
      };
    });
};

/**
 * Format a group's time slot into a human-readable schedule string.
 * Example: "Thursdays 2:00 PM PT"
 */
/**
 * Check whether a participant's available time slots include a group's slot.
 * - No slots on participant → available for all groups
 * - No identifier on group → always available
 * - Otherwise, check for an overlap
 */
export const isParticipantAvailableForGroup = (
  participantSlots: string[] | null,
  groupSlotIdentifier: string | undefined,
): boolean => {
  if (!participantSlots || participantSlots.length === 0) return true;
  if (!groupSlotIdentifier) return true;
  return participantSlots.includes(groupSlotIdentifier);
};

export const formatGroupSchedule = (group: GroupWithMembers): string | undefined => {
  if (!group.time_slot_day || !group.time_slot_time) return undefined;

  const dayMap: Record<string, string> = {
    monday: 'Mondays',
    tuesday: 'Tuesdays',
    wednesday: 'Wednesdays',
    thursday: 'Thursdays',
    friday: 'Fridays',
    saturday: 'Saturdays',
    sunday: 'Sundays',
  };
  const day = dayMap[group.time_slot_day.toLowerCase()] || group.time_slot_day;

  const timeParts = group.time_slot_time.split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  const tz = group.time_slot_timezone ? getTimezoneAbbreviation(group.time_slot_timezone) : '';

  return `${day} ${formattedTime} ${tz}`;
};
